import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  VStack,
  Heading,
  Text,
  Box,
  SimpleGrid,
  LinkBox,
  LinkOverlay,
  useColorMode,
  HStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
} from "@chakra-ui/react";
import { brandConfig, getColor } from "../../brandConfig";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

interface ResearchCard {
  title: string;
  description: string;
  path: string;
  badge?: string;
}

const australiaEnergy: ResearchCard[] = [
  {
    title: "Australia's Oil & Fuel Reserves Crisis",
    description:
      "36 days petrol, 34 days diesel, 32 days jet fuel. How the war is affecting fuel security and prices.",
    path: "/energy-research/australia-oil-reserves",
    badge: "EXCALIDRAW",
  },
  {
    title: "Where Australia Gets Its Petrol & Oil",
    description:
      "Import sources (Middle East 35-40%, SE Asia 25%), only 2 refineries left, Strait of Hormuz dependency.",
    path: "/energy-research/australia-oil-sources",
    badge: "EXCALIDRAW",
  },
  {
    title: "Oil & Fuel Reserves By State",
    description:
      "QLD's Lytton refinery (60% of fuel), which states have no refinery, terminal locations, port imports.",
    path: "/energy-research/australia-oil-reserves-by-state",
    badge: "EXCALIDRAW",
  },
  {
    title: "Australia's Key Decision Makers",
    description:
      "Chris Bowen, Meg O'Neill, Frank Calabria, Andrew Forrest, NOPSEMA, Angus Taylor -- who controls fuel policy.",
    path: "/energy-research/australia-decision-makers",
    badge: "NEW",
  },
];

const geopolitics: ResearchCard[] = [
  {
    title: "Key Australian Leaders: Israel-Iran Positions",
    description:
      "Who supports what? Albanese, Wong, Dutton, Greens -- every leader's stance on Israel, Iran strikes, and Palestine.",
    path: "/energy-research/australia-key-leaders",
    badge: "EXCALIDRAW",
  },
  {
    title: "Everything Australia Does to Support Israel",
    description:
      "F-35 parts (68 shipments, A$4B+), arms exports, intelligence sharing, defence MOUs -- with costs and timelines.",
    path: "/energy-research/australia-israel-support",
    badge: "EXCALIDRAW",
  },
  {
    title: "Immediate Actions: What Australia Is Doing Now",
    description:
      "Active military deployments (Al Minhad, C-17A, KC-30A), Pine Gap intelligence, diplomatic actions as of March 2026.",
    path: "/energy-research/australia-immediate-actions",
    badge: "EXCALIDRAW",
  },
  {
    title: "F-35 Companies: Who Makes the Parts for Israel",
    description:
      "70+ AU companies (BAE, Rosebank, Quickstep, Marand, Ferra, EOS), supply chain route, leaked shipping records.",
    path: "/energy-research/australia-f35-companies",
    badge: "EXCALIDRAW",
  },
];

const japanEnergy: ResearchCard[] = [
  {
    title: "Japan's Energy Crisis Overview",
    description:
      "240-day reserves, ESPO pipeline, US crude imports, METI/JOGMEC strategy, and how Japan compares to Australia.",
    path: "/energy-research/japan-energy-crisis",
    badge: "NEW",
  },
  {
    title: "Japan's Key Decision Makers",
    description:
      "Koichi Hagiuda, METI, JOGMEC, Eneos/Murofushi, IEEJ/Tsunoda -- who controls Japan's energy strategy.",
    path: "/energy-research/japan-decision-makers",
    badge: "NEW",
  },
];

const supplyChain: ResearchCard[] = [
  {
    title: "Key Refineries (Global)",
    description:
      "S-Oil, GS Caltex, Formosa, Hengli, Viva Geelong, ExxonMobil -- capacity, owners, and who supplies AU & JP.",
    path: "/energy-research/key-refineries",
    badge: "NEW",
  },
  {
    title: "Alternative Supply Routes",
    description:
      "US Pacific (14 days), Cape of Good Hope (25-32 days), Russia ESPO, West Africa -- bypassing Hormuz & Malacca.",
    path: "/energy-research/alternative-supply-routes",
    badge: "NEW",
  },
  {
    title: "Relationship Map",
    description:
      "Clickable connections: AU leaders <-> JP leaders, ports <-> refineries <-> shipping routes.",
    path: "/energy-research/relationship-map",
    badge: "NEW",
  },
];

const researchTools: ResearchCard[] = [
  {
    title: "Research Checklist",
    description:
      "All the questions still to research -- port data, reserves, costs, policy -- organised and trackable.",
    path: "/energy-research/research-checklist",
    badge: "CHECKLIST",
  },
];

const Section: React.FC<{
  title: string;
  subtitle: string;
  cards: ResearchCard[];
}> = ({ title, subtitle, cards }) => (
  <>
    <Box mt={8}>
      <Heading size="md" color="#dc2626" mb={1}>
        {title}
      </Heading>
      <Text color={brandConfig.colors.text.secondary} fontSize="sm" mb={4}>
        {subtitle}
      </Text>
    </Box>
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
      {cards.map((card) => (
        <LinkBox
          key={card.path}
          p={6}
          bg={brandConfig.colors.background.surface}
          borderRadius="lg"
          border="1px solid"
          borderColor={brandConfig.colors.border.light}
          _hover={{
            borderColor: "#dc2626",
            shadow: "md",
            transform: "translateY(-2px)",
            transition: "all 0.2s",
          }}
          transition="all 0.2s"
        >
          <VStack align="start" spacing={2}>
            <HStack spacing={2}>
              <LinkOverlay as={RouterLink} to={card.path}>
                <Heading size="sm" color="#dc2626">
                  {card.title}
                </Heading>
              </LinkOverlay>
              {card.badge && (
                <Badge
                  fontSize="2xs"
                  colorScheme={
                    card.badge === "NEW"
                      ? "green"
                      : card.badge === "CHECKLIST"
                      ? "blue"
                      : "gray"
                  }
                >
                  {card.badge}
                </Badge>
              )}
            </HStack>
            <Text fontSize="sm" color={brandConfig.colors.text.secondary}>
              {card.description}
            </Text>
          </VStack>
        </LinkBox>
      ))}
    </SimpleGrid>
  </>
);

const EnergyResearchIndex: React.FC = () => {
  const { colorMode } = useColorMode();
  const bgMain = getColor("background.main", colorMode);
  const textPrimary = getColor("text.primary", colorMode);

  return (
    <Box bg={bgMain} minHeight="100vh" display="flex" flexDirection="column">
      <NavbarWithCallToAction />

      <Container maxW="container.lg" py={10} flex="1">
        <VStack spacing={4} align="stretch">
          <Heading size="lg" color="#dc2626">
            Australia Energy Crisis Research
          </Heading>
          <Text color={brandConfig.colors.text.secondary} fontSize="sm">
            Tracking Australia's fuel vulnerability, supply chain risks, and
            energy security -- updated March 2026
          </Text>

          {/* Live Energy Map Banner */}
          <LinkBox
            p={6}
            bg="rgba(22,163,74,0.08)"
            borderRadius="lg"
            border="2px solid"
            borderColor="#16a34a"
            _hover={{
              shadow: "lg",
              transform: "translateY(-2px)",
              transition: "all 0.2s",
            }}
            transition="all 0.2s"
          >
            <VStack align="start" spacing={2}>
              <HStack spacing={2}>
                <Badge colorScheme="green" fontSize="xs" px={2}>
                  LIVE MAP
                </Badge>
                <Badge colorScheme="gray" fontSize="2xs">
                  NEW
                </Badge>
              </HStack>
              <LinkOverlay as={RouterLink} to="/energy-tracker">
                <Heading size="md" color="#16a34a">
                  National Energy Tracker - Interactive Map
                </Heading>
              </LinkOverlay>
              <Text fontSize="sm" color={brandConfig.colors.text.secondary}>
                Full-screen OpenStreetMap with fuel station markers, supply chain
                flows, trade route visualization, and stress indicators.
                Track fuel supply across Queensland in real-time.
              </Text>
            </VStack>
          </LinkBox>

          {/* Breaking News Banner */}
          <LinkBox
            p={6}
            bg="rgba(220,38,38,0.08)"
            borderRadius="lg"
            border="2px solid"
            borderColor="#dc2626"
            _hover={{
              shadow: "lg",
              transform: "translateY(-2px)",
              transition: "all 0.2s",
            }}
            transition="all 0.2s"
          >
            <VStack align="start" spacing={2}>
              <HStack spacing={2}>
                <Badge colorScheme="red" fontSize="xs" px={2}>
                  BREAKING
                </Badge>
                <Badge colorScheme="gray" fontSize="2xs">
                  22 Mar 2026
                </Badge>
              </HStack>
              <LinkOverlay as={RouterLink} to="/energy-research/iran-hormuz-update">
                <Heading size="md" color="#dc2626">
                  Iran's Selective Blockade: Japan Gets Hormuz Access, Australia Still Blocked
                </Heading>
              </LinkOverlay>
              <Text fontSize="sm" color={brandConfig.colors.text.secondary}>
                Iran will allow Japanese ships through Hormuz. 22 nations form coalition. Trump threatens to
                "obliterate" Iran's power plants in 48hrs. 107+ NSW fuel stations dry. Brent crude at $126/barrel.
                Does the Japan deal help Australia? Full analysis inside.
              </Text>
            </VStack>
          </LinkBox>

          {/* Key Stats */}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mt={4}>
            <Stat
              p={4}
              bg={brandConfig.colors.background.surface}
              borderRadius="lg"
              border="1px solid"
              borderColor={brandConfig.colors.border.light}
            >
              <StatLabel color={brandConfig.colors.text.secondary} fontSize="xs">
                AU Reserves
              </StatLabel>
              <StatNumber color="#dc2626" fontSize="2xl">
                28 days
              </StatNumber>
              <StatHelpText color={brandConfig.colors.text.secondary} fontSize="xs">
                vs IEA required 90
              </StatHelpText>
            </Stat>
            <Stat
              p={4}
              bg={brandConfig.colors.background.surface}
              borderRadius="lg"
              border="1px solid"
              borderColor={brandConfig.colors.border.light}
            >
              <StatLabel color={brandConfig.colors.text.secondary} fontSize="xs">
                JP Reserves
              </StatLabel>
              <StatNumber color={textPrimary} fontSize="2xl">
                240 days
              </StatNumber>
              <StatHelpText color={brandConfig.colors.text.secondary} fontSize="xs">
                highest in IEA
              </StatHelpText>
            </Stat>
            <Stat
              p={4}
              bg={brandConfig.colors.background.surface}
              borderRadius="lg"
              border="1px solid"
              borderColor={brandConfig.colors.border.light}
            >
              <StatLabel color={brandConfig.colors.text.secondary} fontSize="xs">
                Hormuz Dependency
              </StatLabel>
              <StatNumber color="#dc2626" fontSize="2xl">
                35-40%
              </StatNumber>
              <StatHelpText color={brandConfig.colors.text.secondary} fontSize="xs">
                of AU oil imports
              </StatHelpText>
            </Stat>
            <Stat
              p={4}
              bg={brandConfig.colors.background.surface}
              borderRadius="lg"
              border="1px solid"
              borderColor={brandConfig.colors.border.light}
            >
              <StatLabel color={brandConfig.colors.text.secondary} fontSize="xs">
                AU Refineries
              </StatLabel>
              <StatNumber color="#dc2626" fontSize="2xl">
                2 left
              </StatNumber>
              <StatHelpText color={brandConfig.colors.text.secondary} fontSize="xs">
                Lytton (QLD) + Geelong (VIC)
              </StatHelpText>
            </Stat>
          </SimpleGrid>

          <Section
            title="Australia's Energy Crisis"
            subtitle="Oil reserves, import sources, and state-by-state breakdown"
            cards={australiaEnergy}
          />

          <Section
            title="Geopolitics & Conflict"
            subtitle="Australia's involvement in the Israel-Iran conflict and how it connects to energy security"
            cards={geopolitics}
          />

          <Section
            title="Japan's Energy Crisis"
            subtitle="Japan's fuel strategy, reserves, and decision-makers -- 90% of their oil transits the Strait of Hormuz"
            cards={japanEnergy}
          />

          <Section
            title="Supply Chain & Global"
            subtitle="Refineries, alternative shipping routes, and the relationship map between key players"
            cards={supplyChain}
          />

          <Section
            title="Research Tools"
            subtitle="Track what's been researched and what still needs investigation"
            cards={researchTools}
          />
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default EnergyResearchIndex;
